import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Alert,
  Typography,
} from "components/mui";
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import { calculateBase64Size, calculateTotalAttachmentSize } from "libs/utils/fileUtils";
import { formatFileSize } from "utils/formatFileSize";
import { MAX_ATTACHMENT_SIZE_MB, MAX_ATTACHMENT_SIZE_BYTES } from "constants/canmail";
import { useNotificationProvider } from "hooks";

function AttachmentModal({
  open,
  onClose,
  onConfirm,
  // NEW PROPS for edit mode:
  editMode = false,
  existingAttachments = [],
  onAttachmentsUpdated = null,
}) {
  const [uppyInstance, setUppyInstance] = useState(null);

  // Files staged in the Uppy dashboard during this modal session (raw File wrappers).
  const [stagedFiles, setStagedFiles] = useState([]);

  // The already-saved attachments the user has *not* removed. In edit mode these are
  // rendered as deletable chips; in create mode they are invisible but still count
  // toward the size limit. Either way this list — plus the staged files — is the
  // complete desired final state, so no add/delete index bookkeeping is needed.
  const [keptAttachments, setKeptAttachments] = useState([]);

  const [loading, setLoading] = useState(false);

  const { notifyError, notifyInfo } = useNotificationProvider();

  // Preview modal state
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);

  // Sizes are derived, never stored — storing them is what let the edit-mode total
  // drift out of sync with what was actually staged.
  const keptSize = useMemo(
    () => calculateTotalAttachmentSize(keptAttachments),
    [keptAttachments]
  );
  const stagedSize = useMemo(
    () => stagedFiles.reduce((sum, file) => sum + (file.data?.size || 0), 0),
    [stagedFiles]
  );
  const totalSize = keptSize + stagedSize;
  const overSizeLimit = totalSize > MAX_ATTACHMENT_SIZE_BYTES;

  // Uppy's `file-added` handler is created once (see below) but needs the *current*
  // kept-attachment size to enforce the limit, so mirror it into a ref.
  const keptSizeRef = useRef(0);
  useEffect(() => {
    keptSizeRef.current = keptSize;
  }, [keptSize]);

  // Single reset-on-open path. Previously the `editMode` and `!editMode` branches
  // reset different subsets of state and neither covered "edit mode with zero
  // existing attachments", so stale values leaked between opens.
  // Intentionally keyed on `open` alone: this snapshots the parent's attachments at
  // the moment the modal opens, and must not re-run while the user is editing.
  useEffect(() => {
    if (!open) return;
    setKeptAttachments(existingAttachments || []);
    setStagedFiles([]);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Create Uppy exactly once per modal open. This effect previously also depended on
  // the attachment state, so removing an existing attachment destroyed the dashboard
  // and silently discarded every file the user had staged.
  useEffect(() => {
    if (!open) return;

    const uppy = new Uppy({
      restrictions: { maxNumberOfFiles: null },
      autoProceed: false,
    });

    uppy.on("file-added", (file) => {
      setStagedFiles((prev) => {
        const stagedTotal = prev.reduce((sum, f) => sum + (f.data?.size || 0), 0);
        const projectedTotal = keptSizeRef.current + stagedTotal + file.data.size;

        if (file.data.size > MAX_ATTACHMENT_SIZE_BYTES || projectedTotal > MAX_ATTACHMENT_SIZE_BYTES) {
          const message = projectedTotal > MAX_ATTACHMENT_SIZE_BYTES
            ? `Total attachment size (${formatFileSize(projectedTotal)}) would exceed ${MAX_ATTACHMENT_SIZE_MB}MB limit`
            : `File size (${formatFileSize(file.data.size)}) exceeds ${MAX_ATTACHMENT_SIZE_MB}MB limit`;

          notifyError(message);
          uppy.removeFile(file.id);
          return prev;
        }

        return [...prev, file];
      });
    });

    uppy.on("file-removed", (file) => {
      setStagedFiles((prev) => prev.filter((f) => f.id !== file.id));
    });

    setUppyInstance(uppy);

    return () => {
      uppy.close();
      setUppyInstance(null);
    };
  }, [open]);

  const handleRemoveStagedFile = (id) => {
    // `file-removed` updates state; this only has to tell Uppy.
    if (uppyInstance) {
      uppyInstance.removeFile(id);
    } else {
      setStagedFiles((prev) => prev.filter((file) => file.id !== id));
    }
  };

  // Drop an already-saved attachment from the desired final list. No index is
  // recorded anywhere — the kept list *is* the answer.
  const handleDeleteExistingAttachment = (index) => {
    setKeptAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // NEW: Handle preview
  const handlePreviewAttachment = (attachment) => {
    setPreviewAttachment(attachment);
    setOpenPreviewModal(true);
  };

  const convertFileToAttachment = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file.data);
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          // Remove the "data:*/*;base64," prefix
          const pureBase64 = result.split(",")[1];
          resolve({
            name: file.name,
            type: file.type,
            data: pureBase64,
          });
        } else {
          resolve(result);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleConfirm = async () => {
    setLoading(true);

    try {
      // Re-check against the base64-encoded sizes rather than the raw File sizes the
      // dashboard reports, so the guard matches what is actually persisted.
      const newAttachments = await Promise.all(
        stagedFiles.map((file) => convertFileToAttachment(file))
      );

      const newAttachmentsSize = newAttachments.reduce(
        (sum, attachment) => sum + calculateBase64Size(attachment.data),
        0
      );
      const finalSize = keptSize + newAttachmentsSize;

      if (finalSize > MAX_ATTACHMENT_SIZE_BYTES) {
        notifyError(`Total attachment size (${formatFileSize(finalSize)}) exceeds ${MAX_ATTACHMENT_SIZE_MB}MB limit`);
        setLoading(false);
        return;
      }

      if (editMode) {
        // Hand the parent the complete desired attachment list. It is sent verbatim
        // in the single UpdateScheduledEmail call — no per-attachment add/delete
        // requests, so there are no indices to go stale.
        if (onAttachmentsUpdated) {
          onAttachmentsUpdated([...keptAttachments, ...newAttachments]);
        }

        // Nothing has been persisted yet — the user still has to confirm the update.
        notifyInfo("Attachment changes staged — click Update Email to save them");
      } else if (onConfirm) {
        // Create mode: the parent owns the existing list and appends to it.
        onConfirm(newAttachments);
      }

      setLoading(false);
      onClose();
    } catch (error) {
      console.error("[AttachmentModal] Error processing attachments:", error);
      notifyError(error.message || "Failed to process attachments");
      setLoading(false);
    }
  };

  // Discard everything staged in this session; the open-effect re-seeds from props
  // next time, so there is nothing to reset here.
  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          {editMode ? "Manage Attachments" : "Upload Attachments"}
        </DialogTitle>
        <DialogContent>
          {/* Existing Attachments (Edit Mode) */}
          {editMode && keptAttachments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Existing Attachments
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {keptAttachments.map((attachment, index) => {
                  const sizeBytes = calculateBase64Size(attachment.data);
                  const sizeDisplay = formatFileSize(sizeBytes);

                  return (
                    <Chip
                      key={`existing-${index}`}
                      label={`${attachment.name} (${sizeDisplay})`}
                      onDelete={() => handleDeleteExistingAttachment(index)}
                      onClick={() => handlePreviewAttachment(attachment)}
                      sx={{
                        cursor: "pointer",
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Uppy Dashboard */}
          <Box sx={{mb: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
            {uppyInstance && (
              <Box sx={{
                "& .uppy-Dashboard-AddFiles": { textAlign: "center" }
              }}>
                <Dashboard
                  uppy={uppyInstance}
                  hideUploadButton
                  hideProgressAfterFinish
                  height={300}
                  width={850}
                />
              </Box>
            )}
          </Box>

          {/* New Attachments (being added) */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {stagedFiles.map((file) => (
              <Chip
                key={file.id}
                label={`${file.name} (${formatFileSize(file.data?.size || 0)})`}
                onDelete={() => handleRemoveStagedFile(file.id)}
              />
            ))}
          </Box>

          {/* Size Limit Display */}
          <Box sx={{ mt: 2, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
            {!editMode && keptSize > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Already added:</strong> {formatFileSize(keptSize)} / {MAX_ATTACHMENT_SIZE_MB}MB
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Total: {formatFileSize(totalSize)} / {MAX_ATTACHMENT_SIZE_MB} MB
            </Typography>
            {overSizeLimit && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Total size exceeds {MAX_ATTACHMENT_SIZE_MB}MB limit. Please remove some files.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ mx: 2 }}>
          <Button onClick={handleCancel} color="error" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" disabled={loading || overSizeLimit}>
            {loading ? "Saving..." : editMode ? "Save Changes" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Modal */}
      <AttachmentPreviewModal
        open={openPreviewModal}
        onClose={() => setOpenPreviewModal(false)}
        attachment={previewAttachment}
      />

    </>
  );
}

export default AttachmentModal;
